import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Stats {
  beneficiaries: number;
  open: number;
  pledged: number;
  funded: number;
  funded_30d: number;
}
interface Beneficiary {
  id: number;
  name: string;
  contact: string | null;
  orders_count: number;
  total_spent: number;
  currency: string;
  open_requests: number;
}
interface Req {
  id: number;
  beneficiary: string;
  beneficiary_contact: string | null;
  amount: number;
  currency: string;
  note: string | null;
  status: "open" | "pledged" | "funded" | "cancelled";
  sponsor_name: string | null;
  link: string;
  created_at: string;
}

let session: BvSession;
let currency = "JMD";

const STATUS_TONE: Record<string, string> = { open: "", pledged: "warning", funded: "ok", cancelled: "bad" };

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "heart",
    brandLogo: "/logo.svg",
    title: "Pay on behalf",
    subtitle: `${session.merchant.name || "Your store"} · diaspora sponsorship`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "overview", label: "Overview", icon: "heart", render: renderOverview },
      { id: "beneficiaries", label: "Beneficiaries", icon: "users", render: renderBeneficiaries },
      { id: "requests", label: "Requests", icon: "send", render: renderRequests },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

async function renderOverview(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ stats: Stats; recent: Req[] }>("/api/overview").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load", text: "Please try again." }));
    return;
  }
  const s = data.stats;
  host.append(
    statRow([
      { k: "Beneficiaries", v: String(s.beneficiaries), icon: "users" },
      { k: "Open requests", v: String(s.open), icon: "send" },
      { k: "Pledged", v: String(s.pledged), icon: "heart", tone: Number(s.pledged) > 0 ? "accent" : undefined },
      { k: "Funded · 30d", v: fmtMoney(Number(s.funded_30d), currency), icon: "check", tone: "ok" },
    ]),
  );

  host.append(
    card({
      title: "Import beneficiaries",
      action: h("button", { class: "primary", onClick: () => doSync(host) }, "Import from orders"),
      body: h(
        "p",
        { class: "bv-muted" },
        "Build your beneficiary roster from customers who've paid before, then raise a funding request a sponsor abroad can cover.",
      ),
    }),
  );

  host.append(
    card({
      title: "Recent requests",
      body: data.recent.length
        ? dataTable<Req>({
            columns: [
              { head: "When", cell: (r) => fmtDate(r.created_at) },
              { head: "Beneficiary", cell: (r) => r.beneficiary },
              { head: "Amount", num: true, cell: (r) => fmtMoney(Number(r.amount), r.currency || currency) },
              { head: "Status", cell: (r) => pill(r.status, STATUS_TONE[r.status] || "") },
              { head: "Sponsor", cell: (r) => r.sponsor_name || "—" },
            ],
            rows: data.recent,
          })
        : emptyState({ icon: "send", title: "No requests yet", text: "Raise a funding request from the Beneficiaries tab." }),
    }),
  );
}

async function doSync(host: HTMLElement) {
  const r = await bvApi<{ created: number; beneficiaries: number }>("/api/sync", { method: "POST" }).catch(() => null);
  if (!r) return flash("Import failed", "error");
  flash(
    r.beneficiaries
      ? `${r.created} new beneficiar${r.created === 1 ? "y" : "ies"} imported (${r.beneficiaries} total from paid orders).`
      : "No paid orders with contactable customers yet.",
    r.beneficiaries ? "success" : "info",
  );
  renderOverview(host);
}

async function renderBeneficiaries(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const d = await bvApi<{ beneficiaries: Beneficiary[] }>("/api/beneficiaries").catch(() => ({ beneficiaries: [] as Beneficiary[] }));
  host.innerHTML = "";
  host.append(
    card({
      title: "Beneficiaries",
      body: d.beneficiaries.length
        ? dataTable<Beneficiary>({
            columns: [
              { head: "Name", cell: (b) => b.name },
              { head: "Contact", cell: (b) => b.contact || "—" },
              { head: "Orders", num: true, cell: (b) => String(b.orders_count) },
              { head: "Total spent", num: true, cell: (b) => fmtMoney(Number(b.total_spent), b.currency || currency) },
              { head: "Open", num: true, cell: (b) => (b.open_requests ? pill(String(b.open_requests), "warning") : "—") },
            ],
            rows: d.beneficiaries,
            rowActions: (b) => h("button", { class: "secondary", onClick: () => openRequest(b, host) }, "Request funding"),
          })
        : emptyState({ icon: "users", title: "No beneficiaries yet", text: "Import from orders on the Overview tab." }),
    }),
  );
}

function openRequest(b: Beneficiary, host: HTMLElement) {
  const amount = h("input", { type: "number", min: "1", step: "0.01", value: String(Math.round(Number(b.total_spent) / Math.max(1, b.orders_count)) || 1000) }) as HTMLInputElement;
  const note = h("input", { type: "text", placeholder: "What's this for? (optional)" }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: `Request funding — ${b.name}`,
    body: h(
      "div",
      { class: "bv-stack" },
      field(`Amount (${b.currency || currency})`, amount),
      field("Note", note),
      h("p", { class: "bv-muted" }, "You'll get a shareable link to send to a sponsor abroad."),
    ),
    actions: [
      { label: "Cancel" },
      {
        label: "Create link",
        primary: true,
        onClick: () => {
          const amt = +amount.value;
          if (!amt || amt <= 0) {
            flash("Enter an amount", "error");
            return true;
          }
          bvApi<{ request: Req }>("/api/requests", {
            method: "POST",
            body: JSON.stringify({ beneficiary_id: b.id, amount: amt, note: note.value.trim() || null }),
          })
            .then((r) => {
              close();
              showLink(r.request.link);
              renderBeneficiaries(host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

function showLink(link: string) {
  const input = h("input", { type: "text", value: link, readOnly: true }) as HTMLInputElement;
  const handle = openModal({
    title: "Share this sponsor link",
    body: h(
      "div",
      { class: "bv-stack" },
      h("p", { class: "bv-muted" }, "Send this to the sponsor. They can open it and pledge to cover the payment."),
      input,
    ),
    actions: [
      { label: "Done" },
      {
        label: "Copy link",
        primary: true,
        onClick: () => {
          input.select();
          navigator.clipboard?.writeText(link).then(
            () => flash("Link copied", "success"),
            () => flash("Press ⌘/Ctrl+C to copy", "info"),
          );
          return true;
        },
      },
    ],
  });
}

async function renderRequests(host: HTMLElement) {
  host.innerHTML = "";
  let status = "";
  const list = h("div");
  const states = ["", "open", "pledged", "funded", "cancelled"];
  const btns = states.map((sv) =>
    h(
      "button",
      {
        class: sv === "" ? "secondary" : "ghost",
        onClick: () => {
          status = sv;
          btns.forEach((bb, i) => {
            (bb as HTMLElement).className = states[i] === status ? "secondary" : "ghost";
          });
          load();
        },
      },
      sv === "" ? "All" : sv.charAt(0).toUpperCase() + sv.slice(1),
    ),
  );
  const seg = h("div", { class: "bv-row" }, ...btns);
  const load = async () => {
    list.innerHTML = "";
    list.append(skeletonCard());
    const d = await bvApi<{ requests: Req[] }>(`/api/requests${status ? `?status=${status}` : ""}`).catch(() => ({ requests: [] as Req[] }));
    list.innerHTML = "";
    list.append(
      d.requests.length
        ? dataTable<Req>({
            columns: [
              { head: "Beneficiary", cell: (r) => r.beneficiary },
              { head: "Amount", num: true, cell: (r) => fmtMoney(Number(r.amount), r.currency || currency) },
              { head: "Status", cell: (r) => pill(r.status, STATUS_TONE[r.status] || "") },
              { head: "Sponsor", cell: (r) => r.sponsor_name || "—" },
            ],
            rows: d.requests,
            rowActions: (r) =>
              h(
                "div",
                { class: "bv-row" },
                h("button", { class: "ghost", onClick: () => showLink(r.link) }, "Link"),
                r.status === "open" || r.status === "pledged"
                  ? h("button", { class: "secondary", onClick: () => fundReq(r, load) }, "Mark funded")
                  : null,
              ),
          })
        : emptyState({ icon: "send", title: "No requests", text: "Raise a request from the Beneficiaries tab." }),
    );
  };
  host.append(card({ title: "Funding requests", action: seg, body: list }));
  load();
}

async function fundReq(r: Req, reload: () => void) {
  const ok = await bvApi(`/api/requests/${r.id}/fund`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? `Marked ${fmtMoney(Number(r.amount), r.currency || currency)} for ${r.beneficiary} as funded.` : "Couldn't update", ok ? "success" : "error");
  if (ok) reload();
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
